import React from 'react';
import {
  LayoutDashboard,
  Users,
  MapPin,
  CreditCard,
  Calendar,
  Receipt,
  MessageCircle,
  FileText,
  Settings,
  TrendingUp,
  DollarSign,
  Eye,
  Heart,
  Palette,
  Menu,
  X,
  Bell,
  Mail,
  Smartphone,
} from 'lucide-react';
import { MessageNotificationBadge } from '../Messages/MessageNotificationBadge';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../utils/permissions';

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  isCollapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange, isCollapsed, onToggle }) => {
  const { hasPermission, hasAnyPermission } = usePermissions();

  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: null },
    { id: 'members', label: 'Member Management', icon: Users, permission: PERMISSIONS.VIEW_MEMBERS },
    { id: 'towns', label: 'Town Management', icon: MapPin, permission: PERMISSIONS.MANAGE_USERS },
    { id: 'contributions', label: 'Contributions', icon: CreditCard, permission: PERMISSIONS.VIEW_FINANCES },
    { id: 'assignments', label: 'Monthly Assignments', icon: Calendar, permission: PERMISSIONS.VIEW_FINANCES },
    { id: 'expenses', label: 'Expenses', icon: Receipt, permission: PERMISSIONS.VIEW_FINANCES },
    { id: 'benefits', label: 'Benefits', icon: Heart, permission: [PERMISSIONS.APPROVE_FINANCES, PERMISSIONS.APPROVE_BENEFITS] },
    { id: 'events', label: 'Events', icon: Calendar, permission: null },
    {
      id: 'reports', label: 'Reports', icon: FileText, permission: PERMISSIONS.VIEW_REPORTS, subItems: [
        { id: 'member-reports', label: 'Member Reports', icon: Users },
        { id: 'financial-reports', label: 'Financial Reports', icon: TrendingUp },
        { id: 'expense-reports', label: 'Expense Reports', icon: DollarSign },
        { id: 'event-reports', label: 'Event Reports', icon: Calendar }
      ]
    },
    { id: 'messages', label: 'Messages', icon: MessageCircle, permission: null },
    { id: 'sms', label: 'SMS Center', icon: Smartphone, permission: PERMISSIONS.SEND_SMS },
    { id: 'email', label: 'Email Center', icon: Mail, permission: PERMISSIONS.SEND_SMS },
    { id: 'reminders', label: 'Auto Reminders', icon: Bell, permission: PERMISSIONS.MANAGE_USERS },
    { id: 'approvals', label: 'Financial Approvals', icon: DollarSign, permission: [PERMISSIONS.APPROVE_FINANCES, PERMISSIONS.MANAGE_CONTRIBUTIONS] },
    { id: 'audit', label: 'Audit Trail', icon: Eye, permission: [PERMISSIONS.VIEW_AUDIT_LOGS, PERMISSIONS.MANAGE_USERS] },
    {
      id: 'settings', label: 'Settings', icon: Settings, permission: PERMISSIONS.MANAGE_USERS, subItems: [
        { id: 'settings', label: 'System Settings', icon: Settings },
        { id: 'brand-settings', label: 'Brand Settings', icon: Palette }
      ]
    }
  ];

  const menuItems = allMenuItems.filter(item => {
    if (!item.permission) return true;
    if (Array.isArray(item.permission)) {
      return hasAnyPermission(item.permission);
    }
    return hasPermission(item.permission);
  });

  return (
    <>
      {/* Mobile overlay */}
      {!isCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={(e) => {
            // Only close if clicking the overlay itself, not during scroll
            if (e.target === e.currentTarget) {
              onToggle();
            }
          }}
          onTouchStart={(e) => {
            // Prevent overlay from interfering with touch scrolling
            if (e.target !== e.currentTarget) {
              e.stopPropagation();
            }
          }}
        />
      )}

      <div className={`bg-slate-900 dark:bg-dark-bg-secondary text-white dark:text-dark-text-primary h-screen transition-all duration-300 flex flex-col fixed top-0 left-0 z-50 lg:z-auto ${isCollapsed
          ? 'w-16 -translate-x-full lg:translate-x-0'
          : 'w-64 translate-x-0'
        }`}>
        {/* Header with toggle */}
        <div className="p-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-8">
            {!isCollapsed && (
              <div className="flex items-center space-x-3">
                <div className="bg-blue-600 p-2 rounded-lg">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <span className="text-lg font-semibold">Admin Panel</span>
              </div>
            )}
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-slate-800 dark:hover:bg-dark-bg-tertiary transition-colors"
            >
              {isCollapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Scrollable navigation */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <nav className="space-y-2">
            {menuItems.map((item) => (
              <div key={item.id}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      if (item.id === 'reports' && item.subItems) {
                        onTabChange(item.subItems[0].id);
                      } else {
                        onTabChange(item.id);
                      }
                      // Auto-collapse on mobile after selection
                      if (window.innerWidth < 1024) {
                        onToggle();
                      }
                    } catch (error) {
                      console.error('Navigation error:', error);
                    }
                  }}
                  className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'space-x-3 px-4'} py-3 rounded-lg text-left transition-colors hover:bg-slate-800 dark:hover:bg-dark-bg-tertiary ${activeTab === item.id || (item.subItems && item.subItems.some(sub => activeTab === sub.id))
                      ? 'bg-blue-600 dark:bg-blue-700 text-white'
                      : 'text-slate-300 dark:text-dark-text-secondary'
                    }`}
                  title={isCollapsed ? item.label : undefined}
                >
                  {item.id === 'messages' ? (
                    <MessageNotificationBadge className="flex-shrink-0" />
                  ) : (
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                  )}
                  {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                </button>

                {!isCollapsed && item.subItems && (activeTab === item.id || item.subItems.some(sub => activeTab === sub.id)) && (
                  <div className="ml-6 mt-2 space-y-1">
                    {item.subItems.map((subItem) => (
                      <button
                        key={subItem.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          try {
                            onTabChange(subItem.id);
                            if (window.innerWidth < 1024) {
                              onToggle();
                            }
                          } catch (error) {
                            console.error('Sub-navigation error:', error);
                          }
                        }}
                        className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left transition-colors hover:bg-slate-800 dark:hover:bg-dark-bg-tertiary ${activeTab === subItem.id ? 'bg-blue-500 dark:bg-blue-600 text-white' : 'text-slate-400 dark:text-dark-text-tertiary'
                          }`}
                      >
                        <subItem.icon className="h-4 w-4" />
                        <span className="text-sm">{subItem.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};
